// no-console is disabled for this file via the eslint flat config per-file
// override — see `eslint.config.mjs`. This module is the centralized
// console boundary; the file-level disable directive that used to live
// here is redundant with the override.
import * as Sentry from '@sentry/react'

import { isDev } from '~/env'

/**
 * Centralized logger. The rest of the codebase calls `logger.info(...)`,
 * `logger.warn(...)`, `logger.error(...)` instead of `console.*` directly —
 * the lint rule `no-console` blocks `console.*` everywhere except this file
 * and `lib/sentry`.
 *
 * In dev: prints to console with a `[admin]` prefix.
 * In prod: forwards `info` and `warn` to Sentry breadcrumbs (no PII), and
 * `error` becomes a Sentry capture event.
 */
type LogContext = Readonly<Record<string, unknown>>

function devLog(level: 'log' | 'warn' | 'error', message: string, context?: LogContext): void {
  if (!isDev) return
  const args: unknown[] = [`[admin] ${message}`]
  if (context !== undefined) args.push(context)
  console[level](...args)
}

export const logger = {
  debug(message: string, context?: LogContext): void {
    devLog('log', message, context)
  },

  info(message: string, context?: LogContext): void {
    devLog('log', message, context)
    Sentry.addBreadcrumb({
      level: 'info',
      message,
      ...(context ? { data: context as Record<string, unknown> } : {}),
    })
  },

  warn(message: string, context?: LogContext): void {
    devLog('warn', message, context)
    Sentry.addBreadcrumb({
      level: 'warning',
      message,
      ...(context ? { data: context as Record<string, unknown> } : {}),
    })
  },

  error(message: string, error?: unknown, context?: LogContext): void {
    devLog('error', message, { ...(context ?? {}), error })
    Sentry.captureException(error ?? new Error(message), {
      extra: { message, ...(context ?? {}) },
    })
  },
}
